/**
 * Helpers for the single-page list fetches used across the H2R hooks.
 *
 * Several hooks fetch a list in one request with a fixed `page_size` and render
 * `items` directly, with no pagination controls. That works only while the table
 * is smaller than the page size — and it fails *silently* when it isn't: the UI
 * shows a partial list with no indication anything is missing. The My Team tab
 * did exactly this, displaying 100 of 187 active employees.
 *
 * `warnIfTruncated` turns that silent failure into a visible one during
 * development, so the next table to outgrow its page size is noticed immediately
 * rather than being mistaken for complete data.
 */

export interface PaginationMeta {
  page: number
  page_size: number
  total_items: number
  total_pages: number
}

/**
 * Log a warning when a single-page fetch did not return every row.
 *
 * `label` should name the calling hook or view so the message is actionable.
 * Note the backend caps `page_size` per router (most at 100, employees at 500);
 * raising a request past its router's ceiling returns 422, so the fix for a real
 * overflow is to paginate or to raise that router's cap — not just this number.
 */
export function warnIfTruncated(label: string, pagination?: PaginationMeta): void {
  if (!pagination) return
  const { total_items, page_size, total_pages } = pagination
  if (total_pages > 1 || total_items > page_size) {
    // eslint-disable-next-line no-console
    console.warn(
      `[${label}] Showing ${Math.min(page_size, total_items)} of ${total_items} rows ` +
        `(page_size=${page_size}, total_pages=${total_pages}). ` +
        `This list renders one page with no pagination controls, so the rest are hidden.`
    )
  }
}
